import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { pharmacyConfirmPasswordReset } from '../../../redux/middleware/user/pharmacy/resetPassword';
// import { pharmacyConfirmPasswordReset } from '../../../redux/actions/pharmacy/Account/auth';
import { Box, Button, CircularProgress, TextField, Typography, Alert, InputAdornment, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Get token from URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const tokenFromUrl = queryParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setError('Invalid or expired token');
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await pharmacyConfirmPasswordReset(token, newPassword);
      setSuccessMessage('Password reset successfully!');
      setError('');
      setLoading(false);

      // Redirect to login page after success
      setTimeout(() => {
        navigate('/app/pharmacy/login');
      }, 2000);
    } catch (error) {
      setError('Failed to reset password. Please try again.');
      setSuccessMessage('');
      setLoading(false);
    }
  };

  const toggleShowPassword = () => setShowPassword((prev) => !prev);
  const toggleShowConfirmPassword = () => setShowConfirmPassword((prev) => !prev);

  const textFieldStyles = {
    backgroundColor: 'rgba(212, 212, 212, 0.56)',
    borderRadius: '4px',
    borderBottom: '1.5px solid #bdd1e0',
    height: { xs: '2.5em', sm: '2.7em' },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        height: { xs: '2.8em', sm: '3em' },
        boxSizing: 'border-box'
      },
      '&:hover fieldset': {
        borderColor: '#6794b6'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#3b76a2',
        height: { xs: '2.8em', sm: '3em' }
      }
    },
    '& .MuiInputLabel-root': {
      color: '#13334c',
      fontSize: { xs: '0.9em', sm: '1em' },
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center'
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#13334c'
    },
    '& .MuiOutlinedInput-input': {
      color: '#000000',
      fontSize: { xs: '0.95em', sm: '1em' },
      padding: { xs: '0.85em 0.5em', sm: '0.7em 0.5em' },
      display: 'flex',
      alignItems: 'center'
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: 2,
        backgroundColor: '#f7f7f7'
      }}
    >
      <Box
        maxWidth={{ xs: '500px', sm: '600px', md: '600px' }}
        mx='auto'
        my={{ xs: 0.5, sm: 2 }}
        p={{ xs: 3, sm: 3.5 }}
        boxShadow={6}
        borderRadius={2}
        color='#13334c'
        zIndex={1}
        sx={{
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.42)',
          backgroundColor: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0)'
        }}
      >
        <Typography variant='h3' textAlign='center' gutterBottom>
          Reset Password
        </Typography>

        {error && <Alert severity='error'>{error}</Alert>}
        {successMessage && <Alert severity='success'>{successMessage}</Alert>}

        <form onSubmit={handleSubmit}>
          <TextField
            label='New Password'
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            required
            margin='normal'
            sx={textFieldStyles}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton onClick={toggleShowPassword} edge='end' sx={{ color: '#5185ac' }}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            label='Confirm New Password'
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            required
            margin='normal'
            sx={textFieldStyles}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton onClick={toggleShowConfirmPassword} edge='end' sx={{ color: '#5185ac' }}>
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Button
            type='submit'
            variant='contained'
            fullWidth
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Submit New Password'}
          </Button>
        </form>
      </Box>
    </Box>
  );
};

export default ResetPassword;
